import { omit, pick as _pick, isEqual, cloneDeep } from 'lodash';
import { ITextStyle, TextStyle } from 'pixi.js';
import { ResourceData } from '../../../../elements/resource/resource.types';
import {
  AlignParameters,
  AnimationItem,
  AnimationKeys,
  CellAlignment,
  Coords,
  FontAlign,
  FontWeight,
  IWAnimationMetaData,
  Scale,
  ShapeConfig,
  ShapeSelectParams,
  ShowHideAnimation,
  TextAnimationConfig,
  TextAnimationValue,
  TextConfig,
  TextShapeDescriptor,
  Translate,
  TypeDef,
} from '../../../../elements/resource/types/shape.type';
import { RectangleElement } from '../primitive/rectangle-element';
import {
  RCPoint,
  RectangleController,
  RectangleDragResponse,
} from '../../../../elements/util/rectangle-controller/rectangle-controller';
import { ShapeService } from '../../shape.service';
import { selectTextDescriptorByIRI } from '../../../store/selector/editor.selector';
import { PrimitiveShape } from '../primitive/primitive-shape';
import { SVG } from '../primitive/svg-element';
import { addAnimationItemAction } from '../../../animation/store/animation.actions';
import { filter } from 'rxjs/operators';
import { setDescriptorValue } from '../../../store/editor.crud.actions';
import { TextMetrics } from 'pixi.js';
import { PathElement } from '../primitive/path-element';
import { TextLine, TextLineDescriptor } from './text-line';
import { Subscription } from 'rxjs';
import { TextWord } from './text-word';
import { TextLetter } from './text-letter';
import { _screenX, _screenY } from '../../../../services/canvas/canvas.service';
import {
  AutoAnimationFrame,
  BaseAnimationFrame,
  ShapeAnimationState,
} from '../../../animation/components/animation-frame/animation.types';
import { IncrementController } from '../../../animation/frame/increment/controller/increment.controller';
import { RootShape } from '../general/root/root-shape';
import { ContainerShape } from '../container/container.shape';
import { nodeModuleNameResolver } from 'typescript';
import { TextLineNew } from './text-line-new';
import { TextWordNew } from './text-word-new';

export interface CharPosition {
  char: string;
  x: number;
  y: number;
}

export const RC_PADDING = 0;

export class TextShape extends PrimitiveShape<SVG, TextShapeDescriptor> {
  // textElement: Text;

  rect: RectangleElement;

  maskRectangle: RectangleElement;

  getType() {
    return 'text-shape';
  }
  get letters(): TextLetter[] {
    return this.words.reduce((arr, word) => [...arr, ...word.letters], []);
  }
  get newLetters(): TextLetter[] {
    return this._words.reduce((arr, word) => [...arr, ...word.letters], []);
  }
  get _letters(): string[] {
    return this.letters.map(letter => letter.char);
  }
  get words(): TextWord[] {
    return this.lines.reduce((arr, line) => {
      arr.push(...line.words);
      return arr;
    }, []);
  }

  get _words(): TextWordNew[] {
    return this._lines.reduce((arr, line) => {
      arr.push(...line.words);
      return arr;
    }, []);
  }

  get multipleLettersSelected() {
    return (
      this.selectionStartIndex != null &&
      this.selectionStartIndex !== this.currentSelectionIndex
    );
  }

  get textValue(): string {
    if (!this.text) {
      return '';
    }
    const text = this.text;
    if (text.startsWith('$.')) {
      const varName = text.split('.')[1];
      return this.parent.env?.[varName] || text;
    }

    return this.text;
  }

  get dim(): Coords {
    // const { width, height } = this.container.getBounds();
    // return [width, height].map(v => v / this.cs.canvasScale) as Coords;
    const width = this.wordWrap ? this.wordWrapWidth : this.maxLineWidth;

    /* console.log(
      'lineHeight',
      this.lineHeight,
      'baseLineHeight',
      this.baseLineHeight,
    ); */
    if (this.lineHeight < 1) {
      return [
        width,
        this.lineHeight * this.baseLineHeight * (this._lines.length - 1) +
          this.baseLineHeight,
      ];
    }

    return [
      width,
      (this.lineHeight || 1) * this.baseLineHeight * this._lines.length,
    ];
  }

  save() {
    this.saveTranslate();
  }

  // get text() {
  //   const text = this.descriptor.text;
  //   const variable = /(?<={{).*(?=}})/.exec(text);
  //   if (variable?.length) {
  //     return (this.parent as GeneralShape).getVariable(variable[0]);
  //   }
  //   return text;
  // }

  getBounds() {
    return this.container.getBounds();
  }

  get text() {
    return this.textConfig.text;
  }

  set text(value: string) {
    this.textConfig.text = value;
  }

  textConfig: TextConfig;
  get fontSize() {
    return this.textConfig.fontSize;
  }

  set fontSize(value: number) {
    this.textConfig.fontSize = value;
  }

  get wordWrap() {
    return this.textConfig.wordWrap;
  }

  set wordWrap(value: boolean) {
    this.textConfig.wordWrap = value;
  }

  get fontFamily() {
    return this.textConfig.fontFamily;
  }

  set fontFamily(value: string) {
    this.textConfig.fontFamily = value;
  }

  get lineHeight() {
    // return this.textConfig.lineHeight || 1;
    return 1;
  }

  set lineHeight(value: number) {
    this.textConfig.lineHeight = value;
  }

  get wordWrapWidth() {
    return this.textConfig.wordWrapWidth;
  }

  set wordWrapWidth(value: number) {
    this.textConfig.wordWrapWidth = value;
  }

  get letterSpacing() {
    return this.textConfig.letterSpacing || 0;
  }

  set letterSpacing(value: number) {
    this.textConfig.letterSpacing = value;
  }

  get fontStyle() {
    return this.textConfig.fontStyle || 'normal';
  }

  set fontStyle(value: 'normal' | 'italic' | 'oblique') {
    this.textConfig.fontStyle = value;
  }

  get align() {
    return this.textConfig.align || 'left';
  }

  set align(value: FontAlign) {
    this.textConfig.align = value;
  }

  get fontWeight() {
    return this.textConfig.fontWeight || 'normal';
  }

  set fontWeight(value: FontWeight) {
    this.textConfig.fontWeight = value;
  }

  get containerForRc() {
    return this.container;
  }

  get hasFrameRectangle() {
    return true;
  }

  get inputs(): TypeDef {
    return [
      ...super.inputs,
      {
        key: 'text',
        value: 'string',
      },
      {
        key: 'fontSize',
        value: 'string',
      },

      {
        key: 'fontStyle',
        value: 'string',
        options: ['normal', 'italic', 'oblique'],
      },
      {
        key: 'fontWeight',
        value: 'string',
        options: ['normal', 'bold', 'bolder', 'lighter'],
      },
    ];
  }

  // getCurrentBBox(): { width: number; height: number; x: number; y: number } {
  //   this.refreshElement();
  //   const { x, y, width, height } = this.container.getBounds();
  //   return {
  //     x,
  //     y,
  //     width: this.maxLineWidth,
  //     height: this.baseLineHeight * this.lines.length,
  //   };
  // }
  get env() {
    return {
      ...super.env,
      ...{
        text: this.textValue || '',
        fontSize: this.fontSize || '',
        fontStyle: this.fontStyle || '',
        fontWeight: this.fontWeight || '',
      },
    };
  }
  arrowLeftSub: Subscription;
  arrowRightSub: Subscription;
  arrowDownSub: Subscription;
  arrowUpSub: Subscription;
  cursorIndex: string;
  selectionStartIndex: number = null;
  currentSelectionIndex: number = null;
  get currentLetter() {
    return this.newLetters[this.currentSelectionIndex];
  }
  selectionEndIndex: number = null;
  constructor(
    service: ShapeService,
    data: ResourceData<TextShapeDescriptor>,
    config: ShapeConfig,
  ) {
    super(service, data, config);

    // console.log('------ text-shape -----', data); //
    this.textConfig =
      this.descriptor.textConfig ||
      _pick(this.descriptor, [
        'text',
        'fontSize',
        'align',
        'fontStyle',
        'fontWeight',
        'wordWrap',
        'wordWrapWidth',
        'letterSpacing',
        'fontFamily',
      ]);

    // -- // -- //
    // setTimeout(() => {
    //   this.saveKey('text', 'hello world', true);
    // }, 1000);
    if (this.config?.isRoot) {
      this.store
        .select(selectTextDescriptorByIRI(this.IRI))
        .pipe(filter(val => !!val))
        .subscribe(async textConfig => {
          if (isEqual(textConfig, this.textConfig)) {
            return;
          }

          await this.service.fontService._loadFont(textConfig.fontFamily);

          this.textConfig = cloneDeep(textConfig);
          setTimeout(() => {
            this.refreshElement();
          }, 100);
        });
    }

    this.keyEventSubscribe('t+a', () => {
      if (!this.selected) {
        return;
      }
      this.store.dispatch(
        addAnimationItemAction({
          IRI: this.IRI,
          item: {
            key: 'type-animation',
            value: true,
          },
        }),
      );
    });
    this.init();
    if (this.descriptor.hidden) {
      this._hide();
    }

    if (this.config?.isRoot) {
      this.initArrowSubs();
    }

    this.cs.registerKeyDownHandler(key => this.keyDownHandler(key));
  }

  initArrowSubs() {
    // this.arrowRightSub = this.cs.keyDownEventSubscribe('ArrowRight', () => {
    //   this.cursorRightHandler();
    // });
    // this.arrowLeftSub = this.cs.keyDownEventSubscribe('ArrowLeft', () => {
    //   this.cursorLeftHandler();
    // });
    // this.arrowUpSub = this.cs.keyDownEventSubscribe('ArrowUp', () => {
    //   this.cursorUpHandler();
    // });
    // this.arrowDownSub = this.cs.keyDownEventSubscribe('ArrowDown', () => {
    //   this.cursorDownHandler();
    // });
  }

  cursorLeftHandler() {
    // console.log('cursorLeftHandler', this.cs.isShiftPressed);
    if (this.cs.isShiftPressed) {
      if (!this.selectionStartWord) {
        this.setSelectionStartWord();
      }
      this.cursorWord?.cursorLeft();
      this.showSelectionRectangle();
    } else {
      // -- // -- //
      this.currentLetter?.arrowLeft();
    }

    this.updateSelectionIndexes();
  }

  cursorRightHandler() {
    console.log('cursorRightHandler > 1', this.cs.isShiftPressed);
    if (this.cs.isShiftPressed) {
      if (!this.selectionStartWord) {
        this.setSelectionStartWord();
      }
      console.log('cursorRightHandler > 2', this.cursorWord);
      this.cursorWord?.cursorRight();
      this.showSelectionRectangle();
    } else {
      this.cursorWord?.cursorRight();
    }
    this.updateSelectionIndexes();
  }

  getAppearAnimation(): BaseAnimationFrame {
    return {
      type: 'base',
      action: 'appear',
      targetShape: this.IRI,
    };
  }

  getAutoAnimations(): AutoAnimationFrame[] {
    // -- // -- //
    return [];
  }

  async animate() {
    // -- // -- // -- //
    this.words.map(word => word.hide());
    const noSpaces = this.words.filter(word => word.text == ' ');

    for (let i = 0; i < noSpaces.length; i++) {
      await this.timeout(400);
      noSpaces[i].show();
    }
  }

  async timeout(delay: number) {
    await new Promise(res => setTimeout(res, delay));
  }

  cursorDownHandler() {
    if (this.cs.isShiftPressed) {
      if (!this.selectionStartWord) {
        this.setSelectionStartWord();
      }
      if (
        this.selectionStartWord.lineIndex == this.cursorWord.lineIndex &&
        this.cursorWord.selectedIndex < this.selectionStartWordIndex
      ) {
        this.cursorWord.showCursorByIndex(this.selectionStartWordIndex);
        this.removeSelection();
      } else {
        const target = this.cursorWord?.findDownWord(true);
        if (target) {
          const [word, index] = target;
          word.showCursorByIndex(index);
        } else {
          // -- //
          this.cursorWord.line.lastWord.showCursorByIndex();
        }
        this.showSelectionRectangle();
      }
    } else {
      //--//
      const target = this.cursorWord?.findDownWord();
      if (target) {
        const [word, index] = target;
        word.showCursorByIndex(index);
      }
    }
    this.updateSelectionIndexes();
  }

  applyScale(scale: Scale) {
    const { x, y } = scale;
    this.container.scale.set(x as number, y as number);
  }

  startScaleAppearAnimation(scale: 'shrink' | 'grow', division) {
    // -- // -- //
    // -- // -- //
    this.scaleXIncrementController = new IncrementController(0, 1, division);
    this.scaleYIncrementController = new IncrementController(0, 1, division);

    // -- // -- //
    console.log('------------------------------------------');
    console.log('this.maxLineWidth', this.maxLineWidth);
    console.log('this.baseLineHeight', this.baseLineHeight);
    console.log('this.lineHeight', this.lineHeight);
    console.log('this.grossLineHeight', this.grossLineHeight);
    this.translateXIncrementController = new IncrementController(
      this.x + this.maxLineWidth / 2,
      this.x,
      division,
    );
    this.translateYIncrementController = new IncrementController(
      this.y + this.lineHeight / 2,
      this.y,
      division,
    );
  }

  cursorUpHandler() {
    if (this.cs.isShiftPressed) {
      if (!this.selectionStartWord) {
        this.setSelectionStartWord();
      }

      if (
        this.selectionStartWord.lineIndex == this.cursorWord.lineIndex &&
        this.cursorWord.selectedIndex > this.selectionStartWordIndex
      ) {
        this.cursorWord.showCursorByIndex(this.selectionStartWordIndex);
        this.removeSelection();
        return;
      } else {
        const target = this.cursorWord?.findUpWord(true);
        if (target) {
          const [word, index] = target;
          word.showCursorByIndex(index);
        } else {
          this.cursorWord.line.firstWord.showCursorByIndex(0);
        }
        this.showSelectionRectangle();
      }
    } else {
      //--//
      const target = this.cursorWord?.findUpWord();
      if (target) {
        const [word, index] = target;
        word.showCursorByIndex(index);
      }
    }
    this.updateSelectionIndexes();
  }

  setSelectionStartWord() {
    this.selectionStartWord = this.cursorWord;
    this.selectionStartWordIndex = this.cursorWord.selectedIndex;
    // console.log('setSelectionStartWord', this.selectionStartWordIndex);
    // console.log('setSelectionStartWordIndex', this.selectionStartWordIndex);
  }

  selectWord(word: TextWord) {
    word.showCursorByIndex();
    this.selectionStartWord = word;
    this.selectionStartWordIndex = 0;
    this.updateSelectionIndexes();
    this.showSelectionRectangle();
  }

  _selectWord(word: TextWordNew) {
    // word.showCursorByIndex();
    // this.selectionStartWord = word;
    // this.selectionStartWordIndex = 0;
    // this.updateSelectionIndexes();
    // this.showSelectionRectangle();
  }

  unsubscribe() {
    this.arrowRightSub?.unsubscribe();
    this.arrowLeftSub?.unsubscribe();
    this.arrowDownSub?.unsubscribe();
    this.arrowUpSub?.unsubscribe();
  }

  get keys() {
    return ['text', 'fontSize', 'fontStyle', 'fontWeight'];
  }

  doAlign(alignment: CellAlignment, params: AlignParameters = {}) {
    super.doAlign(alignment, params);
    if (!alignment) {
      return;
    }

    const [actualWidth, actualHeight] = this.setFontSizeByAlignment();
    const { save } = params;
    if (save) {
      this.saveKey('fontSize', this.fontSize);
    }

    this.refreshElement();
    return this.alignInCell(this.maxLineWidth, actualHeight);
  }

  setFontSizeByAlignment(): Coords {
    const { w, h, margin, align } = this.cellAlignment;
    // this.fontSize = fontSize;
    // this.refreshElement();
    const _margin = margin || align == 'center' ? 0.3 : 0.1;
    const [xMargin, yMargin] = [w, h].map(val => val * _margin);
    const [width, height] = [w - 2 * xMargin, h - 2 * yMargin];
    let { maxLineWidth: actualWidth, lineHeight: actualHeight } =
      this.measureTextMetric(this.text, this.elementStyle);

    if (actualWidth > width || actualHeight > height) {
      while (actualWidth > width || actualHeight > height) {
        this.fontSize--;

        const metric = this.measureTextMetric(this.text, this.elementStyle);
        /* 
        console.log('---- 1 ----', {
          actualWidth,
          width,
          actualHeight,
          height,
        }); 
        */
        actualWidth = metric.maxLineWidth;
        actualHeight = metric.lineHeight;
      }
    } else {
      // scale up to fit
      while (actualWidth < width && actualHeight < height) {
        this.fontSize++;
        // this.refreshElement();
        /* console.log('---- 2 -------', {
          actualWidth,
          width,
          actualHeight,
          height,
          fontSize: this.fontSize,
          length: this.text.length,
        }); */
        // console.log('fontSize', this.fontSize, 'actualWidth', actualWidth);
        const metric = this.measureTextMetric(this.text, this.elementStyle);
        actualWidth = metric.maxLineWidth;
        actualHeight = metric.lineHeight;
      }
    }
    // -- // -- //
    return [actualWidth, actualHeight];
  }

  detectAutoFontSize(
    container?: { w?: number; h?: number },
    ratio = 0.7,
  ): number {
    const w =
      container?.w ?? this.parent?.cellAlignment?.w ?? this.cellAlignment?.w;
    const h =
      container?.h ?? this.parent?.cellAlignment?.h ?? this.cellAlignment?.h;

    // If no container info available, keep current size
    if (!w && !h) {
      return this.fontSize || 16;
    }

    // Start from at least 1px
    let size = 5;
    let lastGood = Math.max(1, size);

    // Try increasing until we exceed the target ratio; step by 1px for determinism
    // Use measureTextMetric to get width/height for the sample text
    // Guard for runaway loop
    // console.log('w', w, 'h', h, 'ratio', ratio);
    // -- // -- //

    for (let i = 0; i < 2000; i++) {
      const testStyle = { ...this.elementStyle, fontSize: size } as ITextStyle;
      const metric = this.measureTextMetric(this.text, testStyle);
      /* console.log('metric', {
        i,
        maxlineWidth: metric.maxLineWidth,
        lineHeight: metric.lineHeight,
      }); */
      const fitsW = w ? metric.maxLineWidth <= ratio * w : true;
      const fitsH = h ? metric.lineHeight <= ratio * h : true;
      // -- //
      // console.log({ fitsW, fitsH, fontSize: size });
      if (fitsW && fitsH) {
        lastGood = size;
        size += 1;
        continue;
      }
      break;
    }

    return lastGood;
  }

  setAutoFontSize(ratio = 0.7) {
    const w = this.parent?.cellAlignment?.w ?? this.cellAlignment?.w;
    const h = this.parent?.cellAlignment?.h ?? this.cellAlignment?.h;
    const best = this.detectAutoFontSize({ w, h }, ratio);
    this.fontSize = best;
    this.refreshElement();
    return best;
  }

  alignInCell(actualWidth: number, actualHeight: number, noApply?: boolean) {
    const { w, h, x, y, margin, align } = this.cellAlignment;

    /* console.log('alignInCell', {
      actualHeight,
      actualWidth,
      fontSize: this.fontSize,
    }); */
    this.baseLineHeight = actualHeight;
    this.maxLineWidth = actualWidth;
    let newX = x;
    const newY = y + (h - this.baseLineHeight) / 2;

    if (!align || align == 'center') {
      const width = this.wordWrapWidth || this.maxLineWidth;
      newX = x + (w - width) / 2;
    }

    if (noApply) {
      return {
        x: newX,
        y: newY,
      };
    }

    // console.log('-------- noApply --------'); //
    this.translate.x = newX;
    this.translate.y = newY;

    this.applyTranslate(this.translate);
    return this.translate;
  }

  /* applyTranslate(translate: Translate, setTranslate?: boolean): void {
    super.applyTranslate(translate, setTranslate);
    console.log('ts.applyTranslate', translate);
  } */

  enablePointerEvents() {
    this.newLetters.map(letter => letter.enablePointerEvents());
  }
  disablePointerEvents() {
    this.newLetters.map(letter => letter.disablePointerEvents());
  }

  afterInit(): void {
    super.afterInit();

    // console.log('text-shape > afterInit');
    this.container.eventMode = 'passive';

    // -- //
    if (this.fontFamily) {
      this.hide();
      this.service.fontService._loadFont(this.fontFamily).then(() => {
        // console.log('----font-load-ed -----', this.fontFamily);
        this.refreshElement();
        // if (this.descriptor.autoScale) {
        //   // -- //
        //   this.autoScale();
        // }
        this.doAlign(this.descriptor.cellAlignment);
        if (this.hiddenBySetPreAnimationState) {
          return;
        }

        this._show();
        if (this.config?.initial || this.config?.initialType === 'text') {
          setTimeout(() => {
            this.selectWord(this.words[0]);
          }, 1);
        }
      });
    } else {
      // this._show();
      const translate = this.doAlign(this.descriptor.cellAlignment, {
        // noSetDescriptor: true,
      });
      // -- //
    }

    Object.values(this.animationsById || {}).map(animations => {
      if (animations['type-animation']) {
        this.hide();
        this.hiddenByAnimation = true;
      }
    });
  }

  /* _show() {
    console.log('ts._show');
    super._show();
  }

  _hide() {
    console.log('ts._hide');
    super._hide();
  } */

  saveKey(
    innerKey: keyof TextConfig,
    value: number | string | boolean,
    direct = false,
  ) {
    this.store.dispatch(
      setDescriptorValue({
        IRI: this.IRI,
        key: 'textConfig',
        innerKey,
        value,
        direct,
      }),
    );
  }

  updateOpacity(opacity: number) {
    // this.words.map(word => (word.textElement.element.alpha = opacity)); //
    this._show();
    this.container.alpha = opacity;
  }

  envChanged(env: Record<string, any>) {
    super.envChanged(omit(env, this.keys));
    if (!env) {
      return;
    }
    let changed = false;
    this.keys.map(key => {
      if (env[key] && env[key] !== this[key]) {
        this[key] = env[key];
        changed = true;
      }
    });
    if (changed) {
      this.refreshElement();
      this.save();
    }
  }

  typeAnimation: {
    originalText: string;
    current: number;
    division: number;
  };
  start: number;
  prepareKeyValueAnimation(
    animation: AnimationItem,
    division: number,
    duration: number,
  ) {
    super.prepareKeyValueAnimation(animation, division, duration);
    // console.log('text-shape > prepareKeyValueAnimation', animation);
    const { key, value } = animation;
    switch (key as string) {
      case 'fontWeight':
        this.fontWeight = value as 'normal' | 'bold' | 'bolder' | 'lighter';
        this.refreshElement();
        break;
    }
  }

  prepareShowHideAnimation(
    { value: show, x, y, scaleX, scaleY }: ShowHideAnimation,
    division: number,
    duration?: number,
    meta?: IWAnimationMetaData,
  ): void {
    // console.log('--------- prepare-show-hide animation ---------------');
    this.startBaseAnimation(show ? 'appear' : 'disappear', {
      start: 0,
      end: division,
    });
  }

  async baseAnimate(type: 'appear' | 'disappear') {
    const {
      float,
      scale,
      by,
      type: animationType,
    } = this.descriptor.baseAnimations[type] as TextAnimationConfig;

    // -- // -- // -- //
    this.startBaseAnimation(type, {
      start: 0,
      end: 60,
    });

    await this.animationService.animate((increment: number) =>
      this.incrementBaseAnimation(increment),
    );
  }

  textShapeAnimationState: ShapeAnimationState<TextAnimationConfig>;

  startBaseAnimation(
    type: 'appear' | 'disappear',
    { start, end }: { start: number; end: number },
  ) {
    console.log('text-start-base-animation', { start, end });
    super.startBaseAnimation(type, { start, end });
    const division = end - start;
    this.textShapeAnimationState = {
      start,
      end,
      division,
      current: 0,
      state: 'in-progress',
      type,
    };

    // this._letters.map(letter => letter.setMask(this.maskRectangle));
    const overlap = 0.2;

    if (type == 'appear') {
      const { appear } = this.descriptor.baseAnimations || {
        appear: {
          by: 'line',
          type: 'fade',
          motion: {
            scale: 'grow',
          },
        } as TextAnimationConfig,
      };
      console.log('descriptor.baseAnimations', this.descriptor.baseAnimations);
      this._show();

      if (!appear) {
        this.textShapeAnimationState = null;
        return;
      }

      this.textShapeAnimationState.config = appear || {
        type: 'fade',
      };
      const { by, type, scale, float } = appear as TextAnimationConfig;
      // -- // -- // -- //

      console.log('appear', appear);
      switch (by) {
        case 'line':
          // -- // -- // -- //
          this.animationService
            .getWaterfallByOverlap(
              division,
              this.lines.length,
              overlap * division,
            )
            .map((section, index) => {
              this.lines[index].startAnimation(section, appear);
            });

          break;
        case 'letter':
          // const {}

          const letterSections = this.animationService.getWaterfallByOverlap(
            division,
            this.letters.length,
            overlap * division,
          );

          console.log('letter-sections', letterSections);

          // -- // -- // -- //
          this.letters.map((letter, index) => {
            letter.startAnimation(letterSections[index]); // -- // -- //
          });
          break;
        // case 'word': // -- //
        default:
          const words = this.words.filter(word => word.text !== ' ');
          const wordSections = this.animationService.getWaterfallByOverlap(
            division,
            words.length,
            overlap * division,
          );
          words.map((word, index) => {
            word.startAnimation(
              wordSections[index],
              appear as TextAnimationConfig,
            );
          });
          break;
      }
    } else {
      // this.startTextAnimation(disappear, division);
    }
    this._show();
  }

  incrementAnimationByKey(key: AnimationKeys, increment: number): void {
    super.incrementAnimationByKey(key, increment);
    // this.incrementBaseAnimation(increment);
  }

  incrementBaseAnimation(increment: number): boolean {
    super.incrementBaseAnimation(increment);
    if (!this.textShapeAnimationState) {
      return true;
    }

    this.textShapeAnimationState.current += increment;
    const { current, start, end, state, type, config } =
      this.textShapeAnimationState;
    if (state == 'finished') {
      return true;
    }
    if (current < start) {
      return;
    }
    if (end < current && state == 'in-progress') {
      this.endBaseAnimation();
      return true;
    }

    if (type == 'appear') {
      const { type, by } = config as TextAnimationConfig;

      switch (by) {
        case 'line':
          this.lines.map(line => {
            line.incrementAnimation(increment);
          });
          break;
        case 'letter':
          this.letters.map(letter => {
            letter.incrementAnimation(increment);
          });
          break;
        case 'word':
        default:
          this.words
            .filter(word => word.text !== ' ')
            .map((word, index) => {
              word.incrementAnimation(increment, index);
            });
          break;
      }
    }
  }

  endBaseAnimation() {
    super.endBaseAnimation();
    if (this.textShapeAnimationState.state == 'finished') {
      return;
    }

    this.textShapeAnimationState.state = 'finished';

    const { appear } = {
      appear: {
        type: 'fade',
        by: 'word',
      },
    };

    if (this.textShapeAnimationState.type == 'appear') {
      const { by } = appear;

      switch (by) {
        case 'letter':
          this.letters.map(letter => {
            letter.removeMask();
            letter.endAnimation();
          });
          break;
        case 'word':
          this.words
            .filter(word => word.text !== ' ')
            .map(word => {
              word.endAnimation();
            });
          break;
      }
    }
  }
  startAnimation(
    id: string,
    division: number,
    inverse?: boolean,
    duration?: number,
  ): void {
    this.startTextAnimation(
      {
        letterAnimation: {
          type: 'float',
        },
      },
      division,
      inverse,
      duration,
    );
    return;
    Object.values(this.animationsById?.[id] || {}).map(({ key, value }) => {
      switch (key) {
        case 'text':
          this.text = value as string;
          this.refreshElement();
          break;
        case 'fontSize':
          this.fontSize = value as number;
          this.refreshElement();
          break;
        case 'fontWeight':
          this.fontWeight = value as 'normal' | 'bold' | 'bolder' | 'lighter';
          this.refreshElement();
          break;
        case 'type-animation':
          this._show();
          this.start = performance.now();
          this.typeAnimation = {
            originalText: `${this.textValue}`,
            current: 0,
            division: division / this.textValue.length,
          };
          this.text = '';
          this.refreshElement();
          break;
        case 'text-animation':
          const { letterAnimation, wordAnimation } =
            value as TextAnimationValue;
          // -- // -- //

          console.log('--------- x --------');

          break;
      }
    });
    // this.animationsById?.[id]; //
    //super.startAnimation(id, division, inverse, duration);
  }
  wordAnimationIncrement = 0;
  letterAnimationIncrement = 0;
  startTextAnimation(
    value: TextAnimationValue,
    division: number,
    inverse?: boolean,
    duration?: number,
  ) {
    const n = this._letters.filter(char => char !== ' ').length;
    const d = division / (1 / 3 + (2 * n) / 3);
    // this._letters.map((letter, index) => {
    //   letter.startAnimation(d, index);
    // });
  }

  incrementAnimation(
    increment: number,
    id?: string,
    maxIncrement?: number,
  ): void {
    super.incrementAnimation(increment);
    this.incrementBaseAnimation(increment);
    // -- //
    this.letters.map((letter, index) => {
      letter.incrementAnimation(increment);
    });
    if (this.typeAnimation) {
      this.typeAnimation.current += increment;
      const { current, division, originalText } = this.typeAnimation;
      const maxChars = Math.floor(current / division);
      this.text = originalText.slice(0, maxChars);
      this.refreshElement();
    }

    if (this.fillColorIncrement) {
      this.words.map(word =>
        word.textElement.patch({
          fill: this.fillColorIncrement.increment(increment),
        }),
      );
    }
  }

  endAnimationByKeyValue(key: string, value: any): void {
    this.letters.map(letter => {
      letter.endAnimation();
    });
    super.endAnimationByKeyValue(key, value);
    if (this.typeAnimation) {
      this.typeAnimation = null;
      this.text = this.descriptor.text;
      this.refreshElement();
    }
  }

  unMovedRCDiagonal: number;
  unMovedFontSize: number;

  unMovedConfig: TextConfig;
  unMovedWordWrapWidth: number;
  unMovedDim: Coords;

  cursor: PathElement;

  currentText = '';

  selectedEditMode = false;

  baseLineHeight: number;
  init() {
    super.init();

    this.setBaseLineHeight();

    // this.textElement = new TextElement(this, this.container, {
    //   text: this.textValue,
    //   fontSize: this.fontSize,
    // });

    this.refreshElement();

    const [width, height] = this.dim;
    this.maskRectangle = new RectangleElement(this, this.container, {
      width,
      height,
      x: this.x,
      y: this.y,
    });
    // this.textElement
    //   .click(() => {
    //     console.log('te > clicked');
    //     // -- // -- //
    //     this.clicked();
    //   })
    //   .doubleclick(coords => {
    //     console.log('double-click', coords);
    //     const [xa, ya] = coords;
    //     console.log('x', xa - this.x, 'y', ya - this.y);
    //     const [x, y] = [xa - this.x, ya - this.y];

    //     if (!this.selected) {
    //       this.select();
    //     }
    //     // this.wordWrap = false;
    //     // this.wordWrapWidth = Infinity; //
    //     this.selectedEditMode = true;
    //   })
    //   .mouseover(() => {
    //     this.select({ onHover: true });
    //   })
    //   .mouseout(() => {
    //     this.deselect({ onHover: true });
    //   })
    //   .drag(
    //     (dx, dy) => {
    //       this.service.drag(dx, dy);
    //     },
    //     () => this.localStartDrag(),
    //     () => {
    //       // console.log('ps.element > endDrag'); //
    //       this.service.endDrag();
    //     },
    //   );

    // -- //

    // if (this.config?.isRoot) {
    // console.log('init-heyhey', this.x, this.y); // -- // -- // -- // -- //

    this.rc = new RectangleController(
      this,
      {
        width,
        height,
        showRectangle: true,
        noFill: true,
        // symmetric: false,
        // offset: [this.x, this.y],
        scaleMode: true,
        centerBased: true,
        startDrag: p => this.rcStartDrag(p),
        drag: resp => this.rcDrag(resp),
        endDrag: point => this.rcEndDrag(point),
        clicked: () => this.clicked(),
        mouseout: () => {
          // if (!this.cs.shapeAddMode) {
          //   this.deselect({ onHover: true });
          // }
        },
        skipRC: {
          center: true,
        },
      },
      this.circleContainer,
      this.auxCircleContainer,
      this.container,
    );

    this.rc.hide();
    // }

    this.selectedKeyEventSubscribe('f+ArrowUp', () => {
      this.fontSize++;
      this.refreshElement();
    });
    this.selectedKeyEventSubscribe('f+ArrowDown', () => {
      this.fontSize--;
      this.refreshElement();
    });
    this.selectedKeyEventSubscribe('f+ArrowRight', () => {
      this.fontSize += 5;
      this.refreshElement();
      this.cs.consumeKeyEvent('ArrowRight');
    });
    this.selectedKeyEventSubscribe('f+ArrowLeft', () => {
      this.fontSize -= 5;
      this.refreshElement();
      this.cs.consumeKeyEvent('ArrowLeft');
    });

    this.keyEventSubscribe;
  }

  setBaseLineHeight() {
    this.baseLineHeight = this.measureText(
      'abc',
      new TextStyle({
        ...this.elementStyle,
        lineHeight: undefined,
      }),
    ).lineHeight;
  }

  numberOfLines: number;
  minLineHeight: number;
  maxXCoord: number;
  maxYCoord: number;

  rcStartDrag(point?: RCPoint) {
    // -- // -- //
    this.unMovedFontSize = this.fontSize;
    this.unMovedWordWrapWidth = this.wordWrapWidth;
    this.unMovedDim = this.dim as Coords;
    this.unMovedConfig = this.textConfig;
    this.numberOfLines = this._lines.length;
    // -- // -- //
    // console.log('rcStartDrag', this.fontSize);
    // console.log('rcStartDrag.unMovedDim', this.unMovedDim);
    // console.log('rcStartDrag.wordWrapWidth', this.wordWrapWidth);

    this.minLineHeight = this.measureText(
      'abc',
      new TextStyle({
        ...this.elementStyle,
        lineHeight: undefined,
      }),
    ).lineHeight;

    if (point == 'left-center') {
      // in that case we need to restrict x coords
      const currentWidth = this.wordWrap
        ? this.wordWrapWidth
        : this.unMovedDim[0];

      const maxWidthShrink = currentWidth - this.maxWordWidth;
      this.maxXCoord = this.x + maxWidthShrink;
    }

    if (point == 'center-top') {
      // in that case we need to restrict x coords
      const currentHeight = this.unMovedDim[1];

      // -- // -- // -- // -- //
      const minHeight =
        this.baseLineHeight +
        this.baseLineHeight * (this.numberOfLines - 1) * 0.5;
      const maxHeightShrink = currentHeight - minHeight;

      this.maxYCoord = this.y + maxHeightShrink;
    }
  }

  rcDrag({ x, y, dx, dy, width, height, point, scale }: RectangleDragResponse) {
    // -- // -- //
    switch (point) {
      case 'center-top':
      case 'center-bottom':
        // const lineHeight = Math.max(
        //   this.baseLineHeight / 2,
        //   height / this.numberOfLines,
        // );

        if (height / this.numberOfLines < this.baseLineHeight) {
          height = Math.max(
            height,
            this.baseLineHeight +
              this.baseLineHeight * (this.numberOfLines - 1) * 0.5,
          );

          this.rc?.updateRectangle({ height });

          this.lineHeight =
            (height - this.baseLineHeight) /
            (this.baseLineHeight * (this.numberOfLines - 1));
        } else {
          this.lineHeight = height / (this.baseLineHeight * this.numberOfLines);
        }
        this.lineHeight = +this.lineHeight.toFixed(1);
        // console.log('line-height', this.lineHeight);
        this.saveKey('lineHeight', this.lineHeight);

        this.refreshElement(true);

        break;
      case 'left-center':
      case 'right-center':
        this.wordWrap = true;
        this.wordWrapWidth = Math.max(this.maxWordWidth, width);

        // -- //
        //  As the number of lines can change upon the wordWrapWidth the height of the RC might change
        // -- //

        this.refreshElement(true);
        const [, h] = this.dim;
        // if (width < this.maxWordWidth) {

        // }
        this.rc.updateRectangle({
          height: h,
          width: Math.max(width, this.maxWordWidth),
        });

        // -- // -- // -- //

        break;
      default:
        this.fontSize = Math.floor(scale * this.unMovedFontSize);
        if (this.wordWrap) {
          this.wordWrapWidth = Math.max(
            this.maxWordWidth,
            scale * this.unMovedWordWrapWidth,
          );
        }

        // -- // -- //
        this.refreshElement(true);
        break;
    }

    this.dx = dx;
    this.dy = dy;

    /* console.log('apply-translate', {
      x: point == 'left-center' ? Math.min(x, this.maxXCoord) : x,
      y: point == 'center-top' ? Math.min(y, this.maxYCoord) : y,
    }); */
    // -- // -- // -- //
    this.applyTranslate({
      x: point == 'left-center' ? Math.min(x, this.maxXCoord) : x,
      y: point == 'center-top' ? Math.min(y, this.maxYCoord) : y,
    });

    if (
      this.parent instanceof ContainerShape ||
      this.parent instanceof RootShape
    ) {
      this.alignInCell(this.maxLineWidth, this.baseLineHeight);
    }
  }
  rcEndDrag(point: RCPoint) {
    switch (point) {
      case 'center-top':
      case 'center-bottom':
        if (this.lineHeight < 1) {
          this.rc.patch(
            {
              height:
                this.lineHeight *
                  (this.numberOfLines - 1) *
                  this.minLineHeight +
                this.minLineHeight,
            },
            true,
          );
        } else {
          this.rc.patch(
            {
              height: this.lineHeight * this.numberOfLines * this.minLineHeight,
            },
            true,
          );
        }

        if (this.lineHeight == this.maxLineWidth) {
          this.lineHeight = undefined;
        }

        this.saveKey('lineHeight', this.lineHeight);
        break;
      case 'left-center':
      case 'right-center':
        const [width, height] = this.dim;
        this.rc.patch({ height }, true);
        break;
      default:
        this.saveKey('fontSize', this.fontSize);
        break;
    }

    this.saveKey('wordWrap', this.wordWrap);
    this.saveKey('wordWrapWidth', this.wordWrapWidth);
    this.saveTranslate(this.dx, this.dy);
    this.setBaseLineHeight();
  }
  select(params?: ShapeSelectParams) {
    const { onHover } = params || {};

    if (onHover) {
      // this comes from the text-letter element
      if (!this.selected) {
        this.rc.show({
          withoutPCs: true,
        });
      }
      return;
    }

    super.select(params);
    this.newLetters.map(letter => letter.showRectangles());
    this.rc?.show();
    this.selectWholeText();
  }
  selectEachword() {
    // -- // -- // -- //
  }

  initRC() {
    return;
    this.rc?.hide();
    // -- // -- //
    const [width, height] = this.dim;
    // -- // -- //
    this.rc = new RectangleController(
      this,
      {
        width,
        height,
        showRectangle: true,
        // noFill: true, //
        offset: [this.x, this.y],
        scaleMode: true,
        centerBased: true,
        startDrag: p => this.rcStartDrag(p),
        drag: resp => this.rcDrag(resp),
        endDrag: point => this.rcEndDrag(point),
        clicked: () => this.clicked(),
        mouseout: () => {
          // -- // -- //
          if (!this.cs.shapeAddMode) {
            this.deselect({ onHover: true });
          }
        },
        skipRC: {
          center: true,
        },
      },
      this.circleContainer,
      this.auxCircleContainer,
      this.container,
    );
  }

  updateSelection() {
    // -- // -- // -- // -- // -- //
    const [start, end] = [
      this.currentSelectionIndex,
      this.selectionStartIndex,
    ].sort((a, b) => a - b);

    if (start == end) {
      this.newLetters.map(letter => letter.clearSelection());
      return;
    }

    this.newLetters.map(letter => letter.updateSelection(start, end));
  }

  changeTookPlace = false;
  keyDownHandler(key: string) {
    console.log('keyDownHandler', {
      currentSelection: this.currentSelectionIndex,
      selectionStart: this.selectionStartIndex,
    });
    console.log('---------------------currentLetter', !!this.currentLetter);
    if (key.startsWith('Arrow') && this.currentSelectionIndex == null) {
      this.selectionStartIndex = 0;
      this.currentSelectionIndex = 0;
      this.currentLetter?.showCursor();
      this.updateSelection();
      return;
    }

    switch (key) {
      case 'ArrowUp':
        this.currentLetter?.arrowUp();
        return;
      case 'ArrowDown':
        this.currentLetter?.arrowDown();
        return;
      case 'ArrowLeft':
        console.log('ArrowLeft', !!this.currentLetter);
        this.currentLetter?.arrowLeft();
        return;
      case 'ArrowRight':
        this.currentLetter?.arrowRight();
        return;
      // case 'Enter':
      //   key = '\n';
      //   if (this.cursorWord.lastCursorPositionInLine) {
      //     console.log('----- e-e -------');
      //     key = '\n\n';
      //     this.selectionStartIndex++;
      //     this.selectionEndIndex++;
      //   }
      //   break;
      case 'Space':
      case ' ':
        this.typing(' ');
        break;
      case 'Meta':
      case 'Shift':
        break;
      // case 'Backspace':
      //   key = '';
      //   // -- // -- // -- //
      //   this.selectionStartIndex--;
      //   // increase = 0;
      //   break;
      default:
        this.typing(key);
        break;
    }

    console.log('clearSelection');
    this.clearSelection();
  }

  clearSelection() {
    this.newLetters.map(letter => letter.clearSelection());
  }
  typing(key: string) {
    // if (this.curren == null) {
    //   // console.log('selectionStartIndex is null'); // -- //
    //   return;
    // }

    const backspace = key == 'Backspace';
    const space = key == 'Space';
    const enter = key == 'Enter';

    // -- // -- //
    if (enter) {
      key = '\n';
    }

    if (space) {
      key = ' ';
    }

    if (backspace) {
      key = '';
    }

    let start: number, end: number;

    if (this.multipleLettersSelected) {
      start = Math.min(this.selectionStartIndex, this.currentSelectionIndex);
      end = Math.max(this.selectionStartIndex, this.currentSelectionIndex);
    } else {
      start = this.currentSelectionIndex;
      end = this.currentSelectionIndex;
    }

    // -- // console.log('typing', { start, end, key });

    if (backspace && start == end) {
      start--;
    }

    const preTextValue = this.text.slice(0, start);
    const postTextValue = this.text.slice(end);

    this.text = preTextValue + key + postTextValue;
    // console.log('text', this.text);

    console.log({
      start,
      end,
      multi:
        this.selectionStartIndex != null &&
        this.selectionStartIndex !== this.currentSelectionIndex,
    });

    // this.doAlign(this.cellAlignment);

    if (this.multipleLettersSelected) {
      console.log('------- 1.if ---------', {
        selectionStartIndex: this.selectionStartIndex,
        currentSelectionIndex: this.currentSelectionIndex,
      });

      if (backspace) {
        this.currentSelectionIndex = start;
      } else {
        // -- //
        this.currentSelectionIndex = start + 1;
      }
    } else {
      console.log('------- 1.else ---------');
      if (backspace) {
        // this.currentLetter?.arrowLeft();
        this.currentSelectionIndex--;
      } else {
        this.currentSelectionIndex++;
        // this.currentLetter?.arrowRight();
      }
    }
    // if (this.multipleLettersSelected) //

    this.refreshElement();
    this.newLetters.map(letter => letter.showRectangles());
    // this.refreshElement();
    this.saveKey('text', this.text);
    // this.saveKey('fontSize', this.fontSize);
    this.currentLetter?.showCursor();
    this.selectionStartIndex = this.currentSelectionIndex;
    this.finishUpdate();
  }

  _keyDownHandler(key: string) {
    // this.cs.pressedKeys[key] = 1;

    // -- // -- //
    if (
      this.currentSelectionIndex == null ||
      this.currentSelectionIndex == undefined
    ) {
      return;
    }

    const keys = ['Space', 'Enter', 'Backspace'];
    // -- //
    if (key.length > 1 && !keys.includes(key) && !key.startsWith('Arrow')) {
      return;
    }

    let increase = 1;

    switch (key) {
      case 'ArrowUp':
        this.cursorUpHandler();
        return;
      case 'ArrowDown':
        this.cursorDownHandler();
        return;
      case 'ArrowLeft':
        this.cursorLeftHandler();
        return;
      case 'ArrowRight':
        this.cursorRightHandler();
        return;
      case 'Enter':
        key = '\n';
        increase = 1;
        if (this.cursorWord.lastCursorPositionInLine) {
          console.log('----- e-e -------');
          key = '\n\n';
          this.selectionStartIndex++;
          this.selectionEndIndex++;
        }
        break;
      case 'Space':
      case ' ':
        key = ' ';
        if (this.cursorWord.lastCursorPositionInLine) {
          key = '  ';
          this.selectionEndIndex++;
        }
        break;
      case 'Backspace':
        key = '';
        // -- // -- // -- //
        this.selectionStartIndex--;
        increase = 0;
        break;
    }

    this.cs.consumeKeyEvent(key);

    const preTextValue = this.text.slice(0, this.selectionStartIndex);
    const postTextValue = this.text.slice(this.selectionEndIndex);

    this.text = preTextValue + key + postTextValue;

    /* console.log({
      preTextValue,
      postTextValue,
      startIndex: this.selectionStartIndex,
      endIndex: this.selectionEndIndex,
      text: this.text,
    }); */

    this.selectionStartIndex += increase;

    this.selectionEndIndex = this.selectionStartIndex;

    // console.log('key-down-handler');
    this.selectionStartWord = null;
    this.selectionStartWordIndex = null;

    this.changeTookPlace = true;
    this.saveKey('text', this.text);
    this.finishUpdate();
    // -- //

    // console.log('selectionStartIndex', this.selectionStartIndex);

    this.refreshElement();
  }

  hideDragControllers(): void {
    super.hideDragControllers();
    // this.lines.map(line => line.hideRectangle());
    this.cursor?.hide();

    // this.saveKey('text', this.currentText);
    // if (this.changeTookPlace) {
    //   this.finishUpdate();
    // }

    //  -- // -- //
    // this.selectedEditMode = false;
    // -- // -- //

    this.cursor?.hide();
    // this.cs.deregisterKeyDownHandler();
  }

  deselect(params?: ShapeSelectParams): void {
    super.deselect(params);
    // this.lines.map(line => line.hide());
    this.newLetters.map(letter => letter.hideRectangles());
    this.newLetters.map(letter => letter.hideCursor());
    this.rc?.hide();
  }

  _fontSize: number;
  _wordWrapWidth: number;

  startTransformation(dx?: number, dy?: number): void {
    super.startTransformation(dx, dy);
    this.unMovedDim = this.dim;
    this.unMovedRCDiagonal = Math.sqrt(
      Math.pow(this.unMovedDim[0], 2) + Math.pow(this.unMovedDim[1], 2),
    );
    console.log('ts - startWordWrapWidth', this.wordWrapWidth);

    this.rcStartDrag();
  }

  _scaleX: number;
  _scaleY: number;
  transformRatio: number;
  transformation(scaleX: number, scaleY: number, dx?: number, dy?: number) {
    const [x, y] = this.unMovedDim;
    const length = Math.sqrt(Math.pow(x * scaleX, 2) + Math.pow(y * scaleY, 2));

    this.transformRatio = length / this.unMovedRCDiagonal;

    // (this.fontSize = this.transformRatio * this.fontSize),
    // this.textElement.patch({
    //   fontSize: this.transformRatio * this.fontSize,
    //   wordWrap: this.wordWrap,
    //   wordWrapWidth: this.wordWrap
    //     ? Math.floor(this.transformRatio * this.wordWrapWidth)
    //     : undefined,
    // });

    this.fontSize = Math.floor(this.transformRatio * this.unMovedFontSize);
    if (this.wordWrap) {
      this.wordWrapWidth = Math.max(
        this.maxWordWidth,
        this.transformRatio * this.unMovedWordWrapWidth,
      );
    }

    const [width, height] = this.dim;

    this.rc.updateRectangle({
      width,
      height: height * this.transformRatio,
      // height: h,
      // width: Math.max(width, this.maxWordWidth),
    });

    // -- // -- //
    this.refreshElement(true);
    super.transformation(scaleX, scaleY, dx, dy, true);
  }
  endTransformation(): void {
    super.endTransformation();
    this.fontSize *= this.transformRatio;
    this.saveKey('fontSize', this.fontSize);
    if (this.wordWrap) {
      this.wordWrapWidth *= this.transformRatio;
      this.saveKey('wordWrapWidth', this.wordWrapWidth);
    }
  }
  convertHexToNumber(value: string) {
    return value ? Number(`0x${value.substring(1)}`) : 0;
  }

  selectWholeText() {
    this.selectionStartIndex = 0;
    this.currentSelectionIndex = this.newLetters.length - 1;
    this.updateSelection();
  }

  refresh() {
    super.refresh();
    this.refreshElement();
  }

  // updateText() {
  //   if (this.cs.currentAnimation) {
  //     // -- // -- //
  //   } else {
  //     this._updateText();
  //   }
  // }

  // positions: Array<
  //   {
  //     char: string;
  //     x: number;
  //     y: number;
  //   }[]
  // >;

  lines: TextLine[];

  /**
  It is set by the text-words
  */
  maxWordWidth = 0;
  maxLineWidth = 0;
  _style: TextStyle;

  get elementStyle() {
    const style = {
      fontSize: this.fontSize,
      fontStyle: this.fontStyle,
      // fill: color?.startsWith?.('$') ? this.service.resolveColor(color) : color,
      wordWrap: this.wordWrap,
      wordWrapWidth: this.wordWrapWidth,
      letterSpacing: this.letterSpacing,
      // stroke: stroke ? this.service.resolveColor(stroke.color) : undefined,
      // strokeThickness: stroke?.width, //
      fontFamily: this.fontFamily || 'Arial',
      lineHeight: this.lineHeight,
      // trim: true, // -- //
      fontWeight: this.fontWeight,
      // opacity, // -- //
      align: this.align,
    } as ITextStyle;

    const { fill: fillValue, stroke, opacity } = this.svgAttributes;

    const { color } = fillValue || {};

    const fill = color?.startsWith?.('$')
      ? this.service.resolveColor(color)
      : color;
    if (fill) {
      style.fill = fill;
    }

    return style;
  }

  getWordsOfLine(line: string): string[] {
    if (line == '') {
      return [''];
    }

    const words = line.split(' ');
    const result = words.reduce((arr, word, index) => {
      if (word != '') {
        arr.push(word);
      }

      if (index < words.length - 1) {
        arr.push(' ');
      }
      return arr;
    }, []);
    result.push('\n');
    return result;
  }

  letterWidthMap: Record<string, number> = {};

  getLines(text: string): TextLineDescriptor[] {
    // -- // -- // -- //

    this.letterWidthMap = {};

    console.log('getLines', text);
    const initialLines = text.split('\n');
    console.log('initialLines', initialLines);
    const style = new TextStyle(this.elementStyle);

    const w3 = this.measureText('a a', style).maxLineWidth;
    const w1 = this.measureText('a', style).maxLineWidth;
    const spaceWidth = w3 - 2 * w1;

    let startCharacterIndex = 0;
    if (!this.wordWrap) {
      return initialLines.map((line, index) => {
        const object = {
          words: this.getWordsOfLine(line).map(value => {
            const word = {
              value,
              startCharacterIndex,
              width:
                value == ' '
                  ? spaceWidth
                  : this.measureText(value, style).maxLineWidth,
              letterWidths: Array.from(value).map(char => {
                if (this.letterWidthMap[char]) {
                  return this.letterWidthMap[char];
                }
                this.letterWidthMap[char] = this.measureText(
                  char,
                  style,
                ).maxLineWidth;
                return this.letterWidthMap[char];
              }),
            };
            startCharacterIndex += value.length;
            return word;
          }),
          wrapped: false,
          width: this.measureText(line, style).maxLineWidth,
        };

        startCharacterIndex += 1;
        return object;
      });
    }

    const lines = [];

    const wordWidthMap = {
      '': 0,
      ' ': spaceWidth,
    };

    for (const line of initialLines) {
      // -- // -- // -- //
      const initialWords = this.getWordsOfLine(line);
      console.log({ initialWords, line });
      let lineLength = 0;
      let words: string[] = [];

      if (isEqual(initialWords, [''])) {
        // -- // -- //
        lines.push({
          words: [''],
          width: 0,
          wrapped: false,
        });
      } else {
        for (const word of initialWords) {
          const wordWidth =
            wordWidthMap[word] || this.measureText(word, style).maxLineWidth;

          wordWidthMap[word] = wordWidth;

          lineLength += wordWidth;
          if (this.wordWrapWidth <= lineLength) {
            if (words[words.length - 1] == ' ') {
              words.pop();
            }
            words.push('\n');
            lines.push({ words, wrapped: true });
            words = [];
            if (word !== ' ') {
              words.push(word);
              lineLength = wordWidthMap[word];
            } else {
              lineLength = 0;
            }
          } else {
            words.push(word);
          }
        }

        if (words.length) {
          lines.push({ words, wrapped: false });
        }
      }
    }

    return lines.map(({ words, wrapped }, index) => {
      const line = {
        words: words.map((value: string) => {
          const word = {
            value,
            startCharacterIndex,
            width: wordWidthMap[value],
            letterWidths: Array.from(value).map(char => {
              if (this.letterWidthMap[char]) {
                return this.letterWidthMap[char];
              }
              this.letterWidthMap[char] =
                value == '\n' ? 0 : this.measureText(char, style).maxLineWidth;
              return this.letterWidthMap[char];
            }),
          };
          startCharacterIndex += value.length;
          return word;
        }),
        wrapped,
        width: words.reduce((sum, word) => sum + wordWidthMap[word], 0),
      };
      //
      const lastWord = line.words[line.words.length - 1];

      if (lastWord && lastWord != ' ') {
        startCharacterIndex += 1;
      }

      return line;
    });
  }
  characterWidthMap: Record<string, number> = {};
  _lines: TextLineNew[];
  letterIndex: number;
  currentlySelecedLetter: TextLetter;
  letterClicked(letter: TextLetter) {
    this.currentlySelecedLetter = letter;
    // -- //
    this.newLetters.map(letter => letter.hideCursor());
  }

  getLetterIndex() {
    return this.letterIndex++;
  }

  _initLines(
    lines: {
      words: { text: string; width: number }[];
      width: number;
      wrapped: boolean;
    }[],
  ) {
    // -- // -- // -- // -- // -- //
    this.letterIndex = 0;

    const style = new TextStyle(this.elementStyle);
    const metric = this.measureText('Abc', style);
    // -- //

    const maxLineWidth = Math.max(...lines.map(line => line.width));

    this._lines = lines.map((line, index) => {
      return new TextLineNew(this, line, {
        maxLineWidth: this.wordWrap ? this.wordWrapWidth : maxLineWidth,
        lineHeight: metric.lineHeight,
        actualLineHeight: metric.lineHeight,
        letterIndexOffset: 0,
        style,
        index,
        width: line.width,
      });
    });

    // console.log('lines', lines); //
    return 1;
  }

  mouseEventHandler(
    type: 'arrow-left' | 'arrow-right' | 'arrow-up' | 'arrow-down',
  ) {
    // -- // -- // -- // -- // -- //

    if (this.cs.isShiftPressed) {
      // -- //
    } else {
      // -- //
    }
  }

  _getLines(text: string) {
    // -- // -- // -- //
    this.measureCharacters(text);
    // -- // console.log('text', text); //
    const initialLines = text.split('\n');
    // console.log('initialLines', initialLines);
    const lines = [];

    for (const line of initialLines) {
      if (line == '') {
        lines.push({
          words: [{ text: '\n', width: 0 }],
          width: 0,
          wrapped: false,
        });
        continue;
      }
      const words = this._getWordsOfLine(line);
      // console.log({ words }); //

      const wrappedLines = this.wrapLine(words, this.wordWrapWidth);
      // console.log('wrappedLines', wrappedLines);
      for (const wrappedLine of wrappedLines) {
        // console.log('wrappedLine', wrappedLine);
        lines.push(wrappedLine);
      }
    }
    return lines;
  }

  measureCharacters(text: string) {
    this.characterWidthMap = {};
    const style = new TextStyle(this.elementStyle);
    for (const char of text) {
      if (this.characterWidthMap[char]) {
        continue;
      }
      this.characterWidthMap[char] = this.measureText(char, style).maxLineWidth;
    }
  }

  _getWordsOfLine(line: string) {
    //
    // return line.split(' ');
    const words: string[] = [];
    let currentWord = '';
    // -- // -- //
    for (const char of line) {
      if (char == ' ') {
        if (currentWord) {
          words.push(currentWord);
        }
        words.push(' ');
        currentWord = '';
      } else {
        currentWord += char;
      }
    }
    if (currentWord) {
      words.push(currentWord);
    }
    // -- // -- //
    return words.map(value => ({
      text: value,
      width: Array.from(value).reduce(
        (sum, char) => sum + this.characterWidthMap[char],
        0,
      ),
    }));
  }

  wrapLine(
    inputWords: { text: string; width: number }[],
    lineLength = Infinity,
  ) {
    // -- // -- //
    const lines = [];
    let words: { text: string; width: number }[] = [];
    let currentWidth = 0;

    let lastWord: string;
    let lastWidth: number;

    for (const word of inputWords) {
      const { text, width } = word;
      // -- // -- //

      if (lineLength <= currentWidth + width) {
        if (lastWord == ' ') {
          words.pop();
          currentWidth -= lastWidth;
        }
        words.push({ text: '\n', width: 0 });
        lines.push({ words: words, wrapped: true, width: currentWidth });
        if (text == ' ') {
          words = [];
          currentWidth = 0;
        } else {
          words = [word];
          currentWidth = width;
        }
      } else {
        words.push(word);
        currentWidth += width;
      }

      lastWord = text;
      lastWidth = width;
    }

    if (words.length) {
      words.push({ text: '\n', width: 0 });
      lines.push({ words: words, width: currentWidth });
    }

    return lines;
  }

  measureText(char: string, style: TextStyle) {
    return TextMetrics.measureText(char, style);
  }

  measureTextMetric(char: string, style: ITextStyle) {
    // -- // -- //
    const textStyle = new TextStyle({
      ...style,
      lineHeight: undefined,
    });
    return TextMetrics.measureText(char, textStyle);
  }
  spaceWidth: number;
  netLineHeight: number;
  refreshElement(noRectUpdate = false) {
    // if (!this.textElement) {
    //   return;
    // }

    // console.log('------textShape.refreshElement', this.fontSize);
    const style = this.elementStyle;

    // if (this.lineHeight) {
    //   const lineHeight = this.measureText(
    //     'abc',
    //     new TextStyle({
    //       ...this.elementStyle,
    //       lineHeight: undefined,
    //     }),
    //   ).lineHeight;
    //   console.log('lineHeight', lineHeight, 'elementStyle', this.elementStyle);
    //   // style.lineHeight = lineHeight * this.lineHeight;
    // }

    style.lineHeight = undefined;
    this._style = new TextStyle(style);

    const offset = 0;

    const metric = this.measureText(this.textValue, this._style);

    // old
    // const spaceMetric = this.measureText('a', this._style);

    // new // -- //
    const spaceMetric = this.measureText(' ', this._style);
    this.spaceWidth = spaceMetric.maxLineWidth * 0.6;

    this.maxWordWidth = 0;
    this.maxLineWidth = 0;
    this.lines?.map(line => line.remove());
    this._lines?.map(line => line.remove());

    this.baseLineHeight = metric.lineHeight;

    this.netLineHeight = metric.lineHeight;

    // console.log('----------- textValue -----------', this.textValue); // -- //
    const _lines = this._getLines(this.textValue);
    // console.log('_lines', _lines);
    // console.log('characterWidthMap', this.characterWidthMap);
    this.maxLineWidth = Math.max(..._lines.map(line => line.width));
    // console.log('maxLineWidth', this.maxLineWidth);
    this._initLines(_lines);

    // console.log('characterWidthMap', this.characterWidthMap);
    this._words.map(word => word.afterInit());

    // this.lines = lines.map((line, index) => {
    //   const { words } = line;
    //   this.maxWordWidth = Math.max(
    //     this.maxWordWidth,
    //     Math.max(...words.map(({ width }) => width)),
    //   );

    //   const textLine = new TextLine(this, line, {
    //     maxLineWidth: this.wordWrap ? this.wordWrapWidth : metric.maxLineWidth,
    //     // lineHeight: this.lineHeight ? this.lineHeight : metric.lineHeight,
    //     lineHeight: metric.lineHeight,
    //     actualLineHeight: metric.lineHeight,
    //     letterIndexOffset,
    //     style,
    //     index,
    //     width: line.width,
    //   });

    //   // -- // -- // -- //
    //   const charlength =
    //     words.reduce((sum, word) => {
    //       sum += word.value.length;
    //       return sum;
    //     }, 0) || 1;
    //   letterIndexOffset += charlength + 1;

    //   return textLine;
    // });

    // this.words.map(word => word.afterInit());

    // this.numberOfLines = this.lines.length;
    this.numberOfLines = this._lines.length;

    this.showSelectionRectangle();

    if (noRectUpdate) {
      return;
    }

    this.updateRect();
  }

  autoScale() {
    console.log('------------autoScale--------------');
    this.opacity = 0.01;
    let ratio: number;
    switch (this.words.length) {
      case 1:
        ratio = 0.4;
        break;
      case 2:
        ratio = 0.6;
        break;
      case 3:
        ratio = 0.7;
        break;
      default:
        ratio = 0.8;
        break;
    }

    const refWidth = ratio * _screenX;
    // -- // -- //

    console.log({ refWidth, ratio });
    // console.log('max-line-width', this.maxLineWidth);
    if (this.maxLineWidth < refWidth) {
      while (this.maxLineWidth < refWidth) {
        // -- // -- // -- // -- //
        this.fontSize++;
        // console.log('fontSize', this.fontSize); //
        this.refreshElement();
      }
    }

    // console.log('max-line-height', this.netLineHeight); //

    this.x = (_screenX - this.maxLineWidth) / 2;
    this.y = (_screenY - this.netLineHeight) / 2;

    this.applyTranslate({
      x: this.x,
      y: this.y,
    });
    this.opacity = 1;
  }

  /**************************************************************************
   * ALIGN / SCALE
   *************************************************************************/

  scaleToWidth(width: number) {
    // -- //
    console.log('---- scale-to-width ------', {
      maxLineWidth: this.maxLineWidth,
      width,
    });
    if (this.maxLineWidth == width) {
      return;
    }

    if (this.maxLineWidth < width) {
      while (this.maxLineWidth < width && this.fontSize < 10_000) {
        // -- // -- // -- // -- //
        this.fontSize++;
        this.refreshElement();
      }
    } else {
      while (this.maxLineWidth > width && 0 < this.fontSize) {
        // -- // -- // -- // -- //
        this.fontSize--;
        this.refreshElement();
      }
    }
  }

  alignHorizontally(dims: Coords, align?: 'left' | 'center' | 'right'): number {
    const [, h] = dims;
    // TODO - handle align
    return (h - this.netLineHeight) / 2;
  }

  alignVertically(dims: Coords, align?: 'left' | 'center' | 'right'): number {
    const [w] = dims;
    return (w - this.maxLineWidth) / 2;
  }

  scaleToHeight(height: number) {
    // -- //
  }

  showSelectionElement() {
    this.lines.map(line => line.show());
  }

  updateRect() {
    const [width, height] = this.dim;
    //console.log('dims', { width, height });
    // this.rect?.patch({ width, height });
    this.rc?.patch({ width: width + 2 * RC_PADDING, height }, true);
  }

  contextIsUpdated(): void {
    this.refreshElement();
  }

  patch(key: string, value: any): void {
    super.patch(key, value);
    this.refreshElement();
  }
  cursorWord: TextWord;
  selectionStartWord: TextWord;
  selectionStartWordIndex: number;
  _showCursor(position: { x: number; y: number }, word: TextWordNew) {}
  showCursor(position: { x: number; y: number }, word: TextWord) {
    this.cursorWord = word;
    this.cursor ||= new PathElement(this, this.auxCircleContainer, {
      position: {
        x: 0,
        y: 0,
      },
      elements: [],
      stroke: '#000000',
      'stroke-width': 1,
    });

    this.cursor.patch(
      {
        position,
        elements: [
          {
            type: 'line',
            x: 0,
            y: this.grossLineHeight,
          },
        ],
      },
      true,
    );
    this.updateSelectionIndexes();
  }

  get grossLineHeight() {
    return this.lineHeight
      ? this.lineHeight * this.netLineHeight
      : this.netLineHeight;
  }
  get lineHeightOffset() {
    return (this.grossLineHeight - this.netLineHeight) / 2;
  }
  showSelectionRectangle() {
    if (!this.selectionStartWord) {
      return;
    }

    let startLine: TextLine;
    let endLine: TextLine;

    let startWord: TextWord;
    let startIndex: number;

    let endWord: TextWord;
    let endIndex: number;

    if (this.cursorWord.lineIndex <= this.selectionStartWord.lineIndex) {
      startLine = this.cursorWord.parent;
      endLine = this.selectionStartWord.parent;

      startWord = this.cursorWord;
      startIndex = this.cursorWord.selectedIndex;

      endWord = this.selectionStartWord;
      endIndex = this.selectionStartWordIndex;

      /* console.log('showSelectionRectangle', {
        startWord,
        startIndex,
        endWord,
        endIndex,
      }); */
    } else {
      startLine = this.selectionStartWord.parent;
      endLine = this.cursorWord.parent;

      endWord = this.cursorWord;
      endIndex = this.cursorWord.selectedIndex;

      startWord = this.selectionStartWord;
      startIndex = this.selectionStartWordIndex;
    }
    this.lines.map(line => line.removeSelectorRectangle());

    /* console.log('...........showSelectionRectangle...............', {
      startWord,
      startIndex,
      endWord,
      endIndex,
    }); */

    if (startLine.index == endLine.index) {
      // console.log({ startIndex, endIndex }); // -- // -- // -- //
      startLine.showSelectionRectangle(
        startWord,
        startIndex,
        endWord,
        endIndex,
      );
      return;
    }

    for (let i = startLine.index; i <= endLine.index; i++) {
      switch (i) {
        case startLine.index:
          this.lines[i].showSelectionRectangle(startWord, startIndex);
          break;
        case endLine.index:
          this.lines[i].showSelectionRectangle(null, null, endWord, endIndex);
          break;
        default:
          this.lines[i].showSelectionRectangle();
          break;
      }
    }
  }
  removeSelection() {
    // console.log('remove-selection');
    this.selectionStartWord = null;
    this.selectionStartWordIndex = null;
    this._lines.map(line => line.removeSelectorRectangle());
    this.updateSelectionIndexes();
  }

  updateSelectionIndexes() {
    // -- // -- // -- // -- //

    if (!this.cursorWord) {
      this.selectionStartIndex = null;
      this.selectionEndIndex = null;
      return;
    }

    const v1 =
      this.cursorWord.startCharacterIndex + this.cursorWord.selectedIndex;
    const v2 = !this.selectionStartWord
      ? v1
      : this.selectionStartWord.startCharacterIndex +
        this.selectionStartWordIndex;

    if (v1 <= v2) {
      this.selectionStartIndex = v1;
      this.selectionEndIndex = v2;
      // -- //
    } else {
      this.selectionStartIndex = v2;
      this.selectionEndIndex = v1;
      // -- //
    }
  }
}
